import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Zap, ChevronRight, AtSign, Check, AlertTriangle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

// localStorage key must stay in lockstep with VideoView.jsx + AuthCallback.jsx
const REF_KEY = "neosync_signup_ref";
const REF_TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Pull the sharer's username stashed by VideoView when a visitor arrived
// via `/v/{id}?ref=@name`. Returns null if the entry is expired or
// localStorage is unavailable (private mode). NOTE: still returns even if
// slightly older than TTL — AuthCallback re-validates on send.
function readStashedRef() {
  try {
    const raw = localStorage.getItem(REF_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ref) return null;
    if (parsed.saved_at && (Date.now() - parsed.saved_at) > REF_TTL_MS) return null;
    return String(parsed.ref).replace(/^@/, "").trim().toLowerCase() || null;
  } catch { return null; }
}

function normalizeRef(raw) {
  return (raw || "").replace(/^@/, "").trim().toLowerCase();
}

export default function Login() {
  const navigate = useNavigate();

  // Link-attributed referrer (arrived via `?ref=@user`) — read once on mount.
  const [linkRef] = useState(readStashedRef);
  // Whatever the user is currently typing into the widget.
  const [typedRef, setTypedRef] = useState("");
  // Validation of the typed username against /api/users/{username}.
  // States: "idle" | "checking" | "ok" | "unknown"
  const [checkState, setCheckState] = useState("idle");
  // Snapshot of the display name we found so the confirmation chip reads
  // "@matrix — Matrix Video" instead of just the raw handle.
  const [checkedProfile, setCheckedProfile] = useState(null);

  const normalized = useMemo(() => normalizeRef(typedRef), [typedRef]);
  // Mismatch — typed something, link exists, they don't match.
  const mismatch = !!(linkRef && normalized && normalized !== linkRef);

  // Debounce validation so we don't spam the API on every keystroke.
  useEffect(() => {
    if (!normalized) {
      setCheckState("idle");
      setCheckedProfile(null);
      return;
    }
    // Skip an extra API round-trip if the typed value already matches
    // the link ref — we know that user is real because they shared it.
    if (linkRef && normalized === linkRef) {
      setCheckState("ok");
      setCheckedProfile({ username: normalized });
      return;
    }
    setCheckState("checking");
    setCheckedProfile(null);
    const cancel = { done: false };
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/users/${encodeURIComponent(normalized)}`);
        if (cancel.done) return;
        setCheckedProfile(r.data || { username: normalized });
        setCheckState("ok");
      } catch {
        if (cancel.done) return;
        setCheckState("unknown");
        setCheckedProfile(null);
      }
    }, 350);
    return () => { cancel.done = true; clearTimeout(t); };
  }, [normalized, linkRef]);

  // Resolved ref that will actually be sent to the backend:
  //   • typed value wins if it's a valid username (even on mismatch — the
  //     user explicitly overrode; we already showed a warning)
  //   • otherwise fall back to the link-attributed ref
  //   • otherwise nothing (blank input + no link = no referral)
  const resolvedRef = useMemo(() => {
    if (normalized && checkState === "ok") return normalized;
    if (!normalized && linkRef) return linkRef;
    return null;
  }, [normalized, checkState, linkRef]);

  const handleGoogleLogin = () => {
    // Persist the resolved ref (or explicitly clear it if the user typed
    // an unknown handle) BEFORE we bounce to Google OAuth — the round-trip
    // through auth.emergentagent.com would lose any in-memory state.
    try {
      if (resolvedRef) {
        localStorage.setItem(REF_KEY, JSON.stringify({
          ref: resolvedRef,
          via_video_id: null,
          saved_at: Date.now(),
          user_confirmed: true,
        }));
      }
      // If they typed an unknown username while a link ref is stashed,
      // we intentionally leave the stashed ref alone — @linkref stays
      // credited as the safest fallback for an obvious typo.
    } catch { /* private mode — no-op */ }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl,
    )}`;
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen bg-[#050505] text-white relative overflow-hidden grain"
    >
      {/* ambient glows */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#39FF14]/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-32 h-[520px] w-[520px] rounded-full bg-[#FF007F]/20 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 left-1/2 h-[300px] w-[300px] -translate-x-1/2 rounded-full bg-[#00FFFF]/10 blur-3xl" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* top bar */}
        <header className="flex items-center justify-between px-6 sm:px-10 py-6">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 bg-[#39FF14] flex items-center justify-center">
              <Zap className="h-5 w-5 text-black" strokeWidth={2.5} />
            </div>
            <span
              className="font-display font-black text-2xl tracking-tight glitch neon-text-gold"
              data-text="MatrixShopping"
            >
              MatrixShopping
            </span>
          </div>
          <button
            data-testid="login-explore-btn"
            onClick={() => navigate("/")}
            className="font-mono text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#00FFFF] transition-colors"
          >
            explore as guest →
          </button>
        </header>

        {/* hero */}
        <main className="flex-1 flex items-center justify-center px-6 sm:px-10">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl w-full items-center">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#39FF14] mb-6">
                // signal.detected
              </p>
              <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter mb-6">
                feed the <span className="neon-text-pink">algorithm.</span>
                <br />
                jack into the <span className="neon-text-cyan">stream.</span>
              </h1>
              <p className="text-white/60 text-lg max-w-md mb-10 leading-relaxed">
                Short vertical loops. Endless scroll. Built for night-owls,
                creators, and anyone who lives in the glow of a phone screen.
              </p>

              <div className="flex flex-col gap-4">
                {/* --- Referral widget ---
                    Optional field so users can credit a creator with a 10%
                    boost commission. When they arrived via a shared link
                    we pre-know the referrer and can catch mismatches so
                    nobody accidentally credits the wrong person. */}
                <div
                  data-testid="referral-widget"
                  className="border border-white/10 bg-white/[0.02] p-3 backdrop-blur-sm"
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <label
                      htmlFor="referral-input"
                      className="font-mono text-[10px] uppercase tracking-widest text-white/50"
                    >
                      referred by <span className="text-white/30">(optional)</span>
                    </label>
                    {linkRef && (
                      <span
                        data-testid="referral-link-chip"
                        className="font-mono text-[9px] uppercase tracking-widest text-[#39FF14] border border-[#39FF14]/50 px-1.5 py-0.5"
                      >
                        via link · @{linkRef}
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                    <input
                      id="referral-input"
                      data-testid="referral-input"
                      type="text"
                      autoComplete="off"
                      autoCapitalize="off"
                      spellCheck={false}
                      value={typedRef}
                      onChange={(e) => setTypedRef(e.target.value)}
                      placeholder={linkRef ? `leave blank to keep @${linkRef}` : "creator username"}
                      className="w-full bg-black/40 border border-white/10 focus:border-[#39FF14]/60 focus:outline-none pl-9 pr-9 py-2.5 text-sm text-white placeholder:text-white/25 font-mono transition-colors"
                    />
                    {checkState === "checking" && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40 animate-spin" />
                    )}
                    {checkState === "ok" && (
                      <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#39FF14]" />
                    )}
                    {checkState === "unknown" && (
                      <AlertTriangle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#FF9500]" />
                    )}
                  </div>

                  {/* Contextual feedback */}
                  {mismatch && (
                    <p
                      data-testid="referral-mismatch-warning"
                      className="mt-2 text-[11px] leading-tight text-[#FF007F] flex items-start gap-1.5"
                    >
                      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                      <span>
                        This doesn&apos;t match the link you came from
                        (<span className="font-mono">@{linkRef}</span>).
                        Leave blank to keep @{linkRef}, or clear the box if
                        you meant to override.
                      </span>
                    </p>
                  )}
                  {!mismatch && checkState === "unknown" && (
                    <p
                      data-testid="referral-unknown-warning"
                      className="mt-2 text-[11px] leading-tight text-[#FF9500]"
                    >
                      No creator with that username. Fix it or leave blank
                      to sign up without a referral.
                    </p>
                  )}
                  {!mismatch && checkState === "ok" && checkedProfile && (
                    <p
                      data-testid="referral-ok-confirm"
                      className="mt-2 text-[11px] leading-tight text-[#39FF14]"
                    >
                      @{checkedProfile.username || normalized}
                      {checkedProfile.name ? ` — ${checkedProfile.name}` : ""}
                      {" "}will earn 10% of every boost your videos ever get.
                    </p>
                  )}
                  {!normalized && linkRef && (
                    <p className="mt-2 text-[11px] leading-tight text-white/50">
                      Leave blank to credit <span className="text-[#39FF14] font-mono">@{linkRef}</span>{" "}
                      from your shared link.
                    </p>
                  )}
                  {!normalized && !linkRef && (
                    <p className="mt-2 text-[11px] leading-tight text-white/40">
                      Add a creator&apos;s username if someone told you about <span className="neon-text-gold font-semibold">MatrixShopping</span>.
                    </p>
                  )}
                </div>

                <button
                  data-testid="google-login-btn"
                  onClick={handleGoogleLogin}
                  className="group bg-[#39FF14] text-black font-bold uppercase tracking-wider px-8 py-4 hover:shadow-[0_0_30px_rgba(57,255,20,0.6)] transition-all flex items-center justify-center gap-3"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#000"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#000"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#000"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#000"
                    />
                  </svg>
                  <span className="flex flex-col items-start leading-tight text-left">
                    <span
                      data-testid="login-signin-headline"
                      className="animate-signin-nudge-dark font-extrabold text-lg tracking-widest"
                    >
                      SIGN IN
                    </span>
                    <span className="text-[10px] font-mono normal-case tracking-wide opacity-80">
                      continue with google
                    </span>
                  </span>
                  <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </div>

              <div className="mt-12 flex items-center gap-6 font-mono text-xs text-white/40">
                <span>// E2E encrypted</span>
                <span>// no spam</span>
                <span>// instant access</span>
              </div>
            </div>

            <div className="hidden lg:flex flex-col gap-4 relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="aspect-[9/16] bg-[#0A0A0C] border border-white/10 relative overflow-hidden group">
                  <img
                    src="https://images.unsplash.com/photo-1542902093-d55926049754?crop=entropy&cs=srgb&fm=jpg&q=80&w=400"
                    alt=""
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="font-mono text-[10px] text-[#39FF14] uppercase tracking-widest">
                      #nightcore
                    </p>
                    <p className="text-white text-sm font-semibold mt-1">
                      midnight in tokyo
                    </p>
                  </div>
                </div>
                <div className="aspect-[9/16] bg-[#0A0A0C] border border-white/10 relative overflow-hidden mt-12 group">
                  <img
                    src="https://images.unsplash.com/photo-1519608487953-e999c86e7455?crop=entropy&cs=srgb&fm=jpg&q=80&w=400"
                    alt=""
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="font-mono text-[10px] text-[#FF007F] uppercase tracking-widest">
                      #cyberwalk
                    </p>
                    <p className="text-white text-sm font-semibold mt-1">
                      neon avenue
                    </p>
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 font-mono text-xs text-white/30 uppercase tracking-widest">
                <span className="neon-text-green">●</span> live · 12.4k watching
              </div>
            </div>
          </div>
        </main>

        <footer className="px-6 sm:px-10 py-6 font-mono text-xs uppercase tracking-[0.2em] text-white/30 flex justify-between">
          <span>v1.0 · build:0xA1F</span>
          <span>made for the night</span>
        </footer>
      </div>
    </div>
  );
}
